import React from "react";

import { Text, useTheme, Image } from "@bookingcom/bui-react";
import Frame from "../../Frame";
import styles from "./FlightCardHighlight.module.css";
import useUserAgent from "../../../../hooks/useUserAgent";
import { useI18n } from "@bookingcom/lingojs-react";
import { t } from "@bookingcom/lingojs-core";
import FlightCardGeniusHighlightBottomSheetWrapper from "./GeniusPinning/FlightCardGeniusHighlightBottomSheetWrapper";
import FlightCardGeniusHighlightTooltipWrapper from "./GeniusPinning/FlightCardGeniusHighlightTooltipWrapper";
import type { HighlightInfoTooltip } from "./GeniusPinning/types";

type Props = {
  style?: FlightCardHighlightStyle;
  text: string;
  textEmphasized?: boolean;
  showGeniusLogo?: boolean;
  infoTooltip?: HighlightInfoTooltip;
};

export const FlightCardHighlightStyle = {
  PRIMARY: "PRIMARY",
  CONSTRUCTIVE: "CONSTRUCTIVE",
  GENIUS: "GENIUS",
  NEUTRAL: "NEUTRAL"
} as const;

export type FlightCardHighlightStyle = typeof FlightCardHighlightStyle[keyof typeof FlightCardHighlightStyle];

export const FlightCardHighlight = ({
  style = FlightCardHighlightStyle.PRIMARY,
  text,
  textEmphasized = false,
  showGeniusLogo = false,
  infoTooltip
}: Props) => {
  const { colors } = useTheme();

  const getBackgroundColor = (style: FlightCardHighlightStyle) => {
    return style === FlightCardHighlightStyle.CONSTRUCTIVE
      ? colors.color_constructive_background_alt
      : style === FlightCardHighlightStyle.GENIUS
      ? colors.color_brand_genius_primary_background
      : style === FlightCardHighlightStyle.NEUTRAL
      ? colors.color_background_base
      : colors.color_action_background_alt;
  };

  const getForegroundColor = (style: FlightCardHighlightStyle) => {
    return style === FlightCardHighlightStyle.CONSTRUCTIVE
      ? colors.color_constructive_foreground
      : style === FlightCardHighlightStyle.GENIUS
      ? colors.color_foreground_inverted
      : colors.color_brand_primary_foreground;
  };

  const getBorderColor = (style: FlightCardHighlightStyle) => {
    return style === FlightCardHighlightStyle.NEUTRAL ? colors.color_border_alt : undefined;
  };

  const { isMobile } = useUserAgent();
  const i18n = useI18n();

  return (
    <Frame
      alignItems="center"
      direction="row"
      justifyContent="space-between"
      m={0}
      attributes={{
        style: { background: getBackgroundColor(style), borderBottom: `1px solid ${getBorderColor(style)}` }
      }}
    >
      <Frame direction="row" alignItems="center">
        {showGeniusLogo && (
          <Frame ml={4}>
            <Image
              asset={{
                setName: "images-genius",
                assetName: "GeniusLogo"
              }}
              height="12px"
              alt={i18n.trans(t("a11y_genius_sr_pinned_deal"))}
            />
          </Frame>
        )}
        <Text
          className={styles.highlightText}
          variant={textEmphasized ? "emphasized_2" : "body_2"}
          attributes={{
            style: {
              color: getForegroundColor(style),
              background: getBackgroundColor(style)
            }
          }}
        >
          {text}
        </Text>
      </Frame>

      {infoTooltip &&
        (isMobile ? (
          <FlightCardGeniusHighlightBottomSheetWrapper infoTooltip={infoTooltip} />
        ) : (
          <FlightCardGeniusHighlightTooltipWrapper infoTooltip={infoTooltip} />
        ))}
    </Frame>
  );
};
